// PBR shared scroll-reveal helper.
// Adds `data-pbr-reveal-ready` to each `.pbr-section` (so CSS only hides
// `.pbr-reveal` elements when JS is active — no FOUC / no hidden content if
// JS fails), then toggles `.is-revealed` as elements enter the viewport.
// Elements already on screen when this runs have been visible since first
// paint, so they are marked revealed in the same write as the ready flag —
// hiding them and animating them back in would read as a double load.
// Idempotent: safe to load from multiple PBR sections on one page.
(() => {
  if (window.__pbrRevealInit) return;
  window.__pbrRevealInit = true;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const overlapsViewport = (rect) =>
    rect.top < window.innerHeight && rect.bottom > 0;

  const init = () => {
    const sections = document.querySelectorAll('.pbr-section');
    if (!sections.length) return;

    // Reduced motion or no observer support: reveal everything immediately.
    if (prefersReduced.matches || !('IntersectionObserver' in window)) {
      sections.forEach((section) => {
        section
          .querySelectorAll('.pbr-reveal')
          .forEach((el) => el.classList.add('is-revealed'));
      });
      return;
    }

    const onIntersect = (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(onIntersect, {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1,
    });

    // Late variant for tall elements whose animation should only run once a
    // good chunk of them is on screen (opt in with data-pbr-reveal-late).
    const lateObserver = new IntersectionObserver(onIntersect, {
      rootMargin: '0px 0px -18% 0px',
      threshold: 0.35,
    });

    sections.forEach((section) => {
      if (section.dataset.pbrRevealReady) return;
      const els = Array.from(section.querySelectorAll('.pbr-reveal'));

      // Measure BEFORE flagging the section ready. The ready flag is what
      // lets the section CSS hide `.pbr-reveal`; a layout read after it would
      // flush that hidden style, and on-screen elements would then blink out
      // and transition back in from opacity 0.
      const rects = els.map((el) => el.getBoundingClientRect());

      section.dataset.pbrRevealReady = 'true';
      els.forEach((el, i) => {
        if (overlapsViewport(rects[i])) {
          el.classList.add('is-revealed');
          return;
        }
        const target = 'pbrRevealLate' in el.dataset ? lateObserver : observer;
        target.observe(el);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-run when sections are added/edited in the Theme Editor.
  document.addEventListener('shopify:section:load', () => {
    window.requestAnimationFrame(init);
  });
})();
